import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, Lightbulb } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const LABEL = { approve: "Approve & send (simulated)", reject: "Reject invitation", defer: "Defer invitation" };
const BTN = { approve: "bg-[#047857] text-white", reject: "bg-red-600 text-white", defer: "bg-slate-700 text-white" };

export const DecisionDialog = ({ open, onOpenChange, invitation, action, onDecided }) => {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setNotes(""); setError(""); } }, [open, invitation, action]);

  const submit = async () => {
    if (busy || !invitation) return;
    if (!notes.trim()) { setError("A decision reason is required and is recorded in the audit trail."); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/invitations/${invitation.invitation_id}/decision`, { action, notes: notes.trim() });
      toast.success(`Invitation ${data.new_status}${data.delivery?.label ? ` · ${data.delivery.label}` : ""}`);
      onOpenChange(false); onDecided?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "mfa_required") setError("This action requires an MFA-verified senior session.");
      else setError(formatApiError(d) || "Decision failed.");
    } finally { setBusy(false); }
  };

  if (!invitation) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#047857]" /> {LABEL[action] || action}</DialogTitle>
          <DialogDescription>{invitation.recipient_org} · AI recommendation: <b>{invitation.ai_recommendation || "—"}</b>{invitation.data_origin !== "real" ? ` · ${invitation.data_origin}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-1">
          {invitation.reviewer_assist && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600" data-testid="decision-assist">
              <div className="flex items-center gap-1.5 font-medium text-slate-700 mb-1"><Lightbulb size={13} className="text-amber-500" /> Reviewer assist (advisory only — you decide)</div>
              <div>Suggested next step: <b>{(invitation.reviewer_assist.next_action?.action || "monitor").replace(/_/g, " ")}</b>{invitation.reviewer_assist.stalled_days != null ? ` · stalled ${invitation.reviewer_assist.stalled_days} day(s)` : ""}</div>
              <p className="mt-0.5">{invitation.reviewer_assist.next_action?.rationale}</p>
              <p className="mt-1 text-slate-500"><span className="font-medium">AI reasoning:</span> {invitation.reviewer_assist.ai_reasoning?.explanation}</p>
            </div>
          )}
          {action === "approve" && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700 flex gap-2" data-testid="decision-simulate-notice">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Sends are SIMULATED (PREVIEW / NOT ACTUALLY DELIVERED). No real email leaves the platform. The recipient still flows through Registration → TradeGuard → senior human final approval.
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="dec-notes">Reviewer reason <span className="text-red-500">*</span></Label>
            <Textarea id="dec-notes" data-testid="decision-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Why are you approving / rejecting / deferring this invitation?" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="decision-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="decision-cancel" disabled={busy} onClick={() => onOpenChange(false)} className="h-9 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Cancel</button>
          <button data-testid="decision-submit" disabled={busy || !notes.trim()} onClick={submit} className={`h-9 px-4 rounded-md text-sm disabled:opacity-40 ${BTN[action] || "bg-slate-700 text-white"}`}>{busy ? "Saving…" : "Confirm"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
